/**
 * Five looks on the same openr palette. Each theme is a set of CSS custom properties applied to <html>, plus the
 * ink colours charts need as plain hex (SVG attributes cannot read CSS variables).
 */
export type ThemeId = 'soft' | 'obsidian' | 'aurora' | 'paper' | 'neon';

export interface ChartInk {
  primary: string;
  secondary: string;
  grid: string;
  surface: string;
  series: string[];
}

export interface Theme {
  id: ThemeId;
  name: string;
  tagline: string;
  dark: boolean;
  vars: Record<string, string>;
  ink: ChartInk;
}

const brand = {
  blueberry: '#312783',
  grape: '#b378b3',
  honey: '#e4ae50',
  kale: '#2b6a6b',
  mint: '#7fb8b9',
  tomato: '#cc0e0e',
  liquorice: '#242424',
  caviar: '#6a6a6a',
  anchovy: '#c8c8c8',
  earlGrey: '#f6f6f6',
  salt: '#ffffff',
  frosting: '#eae9f2',
  frostingLight: '#f5f4f9',
  frostingDark: '#d7d6e7',
};

const gradient = `linear-gradient(90deg, ${brand.blueberry} 0%, ${brand.grape} 33%, ${brand.mint} 66%, ${brand.honey} 100%)`;

const lightSeries = ['#5146c1', '#0f9ea1', '#b7801a', '#b06bb0', '#cf5d35'];
const darkSeries = ['#8d85e3', '#3fc3c6', '#e0a63f', '#d49ad4', '#f0855f'];

const lightInk: ChartInk = {
  primary: brand.liquorice,
  secondary: brand.caviar,
  grid: brand.frosting,
  surface: brand.salt,
  series: lightSeries,
};

export const THEMES: Theme[] = [
  {
    id: 'soft',
    name: 'Soft',
    tagline: 'Light, rounded, frosted tints. The default.',
    dark: false,
    vars: {
      '--bg': '#f3f3f8',
      '--bg-pattern': 'none',
      '--surface': brand.salt,
      '--surface-2': brand.frostingLight,
      '--surface-border': 'transparent',
      '--text': brand.liquorice,
      '--text-muted': brand.caviar,
      '--text-faint': brand.anchovy,
      '--nav-bg': brand.liquorice,
      '--nav-text': brand.anchovy,
      '--nav-text-active': brand.salt,
      '--nav-active-text': brand.salt,
      '--nav-active-bg': 'rgba(255,255,255,0.1)',
      '--nav-border': 'rgba(255,255,255,0.06)',
      '--accent': brand.blueberry,
      '--accent-soft': '#5146c1',
      '--accent-gradient': '#5146c1',
      '--shadow': '0 1px 2px rgba(36,36,36,0.04), 0 8px 24px rgba(49,39,131,0.06)',
      '--radius': '16px',
      '--chip-bg': brand.earlGrey,
      '--hero-bg': brand.liquorice,
      '--hero-text': brand.salt,
      '--content-radius': '24px',
      '--blur': 'none',
    },
    ink: lightInk,
  },
  {
    id: 'obsidian',
    name: 'Obsidian',
    tagline: 'Full dark. Glass cards with a gradient glow.',
    dark: true,
    vars: {
      '--bg': '#161616',
      '--bg-pattern':
        'radial-gradient(60% 50% at 80% 0%, rgba(49,39,131,0.35) 0%, rgba(49,39,131,0) 70%)',
      '--surface': 'rgba(255,255,255,0.045)',
      '--surface-2': 'rgba(255,255,255,0.06)',
      '--surface-border': 'rgba(255,255,255,0.08)',
      '--text': '#f2f1f7',
      '--text-muted': '#a6a4b3',
      '--text-faint': '#5c5a68',
      '--nav-bg': '#0f0f10',
      '--nav-text': '#8f8d9c',
      '--nav-text-active': brand.salt,
      '--nav-active-text': brand.salt,
      '--nav-active-bg': gradient,
      '--nav-border': 'rgba(255,255,255,0.06)',
      '--accent': brand.grape,
      '--accent-soft': '#a89ff0',
      '--accent-gradient': gradient,
      '--shadow': '0 0 0 1px rgba(255,255,255,0.04), 0 20px 50px rgba(0,0,0,0.5)',
      '--radius': '18px',
      '--chip-bg': 'rgba(255,255,255,0.08)',
      '--hero-bg': '#0f0f10',
      '--hero-text': brand.salt,
      '--content-radius': '28px',
      '--blur': 'blur(18px)',
    },
    ink: {
      primary: '#f2f1f7',
      secondary: '#a6a4b3',
      grid: 'rgba(255,255,255,0.08)',
      surface: '#1c1c1e',
      series: darkSeries,
    },
  },
  {
    id: 'aurora',
    name: 'Aurora',
    tagline: 'The openr gradient as a living background, cards of frosted glass.',
    dark: false,
    vars: {
      '--bg': '#eef0f6',
      '--bg-pattern': `radial-gradient(103% 108% at 2% 4%, rgba(49,39,131,0.22) 10%, rgba(179,120,179,0.22) 35%, rgba(127,184,185,0.22) 65%, rgba(228,174,80,0.22) 90%)`,
      '--surface': 'rgba(255,255,255,0.62)',
      '--surface-2': 'rgba(255,255,255,0.55)',
      '--surface-border': 'rgba(255,255,255,0.75)',
      '--text': brand.liquorice,
      '--text-muted': '#5d5b6b',
      '--text-faint': '#a9a7b8',
      '--nav-bg': 'rgba(36,36,36,0.92)',
      '--nav-text': brand.anchovy,
      '--nav-text-active': brand.salt,
      '--nav-active-text': brand.salt,
      '--nav-active-bg': gradient,
      '--nav-border': 'rgba(255,255,255,0.08)',
      '--accent': brand.blueberry,
      '--accent-soft': '#5146c1',
      '--accent-gradient': gradient,
      '--shadow': '0 10px 40px rgba(49,39,131,0.12)',
      '--radius': '20px',
      '--chip-bg': 'rgba(255,255,255,0.7)',
      '--hero-bg': 'rgba(36,36,36,0.9)',
      '--hero-text': brand.salt,
      '--content-radius': '0',
      '--blur': 'blur(20px)',
    },
    ink: { ...lightInk, grid: 'rgba(36,36,36,0.08)', surface: '#f7f7fb' },
  },
  {
    id: 'paper',
    name: 'Paper',
    tagline: 'Editorial. White, big type, hairlines, no shadows.',
    dark: false,
    vars: {
      '--bg': brand.salt,
      '--bg-pattern': 'none',
      '--surface': brand.salt,
      '--surface-2': '#fafafa',
      '--surface-border': '#e6e6e6',
      '--text': '#111111',
      '--text-muted': '#6a6a6a',
      '--text-faint': '#bdbdbd',
      '--nav-bg': brand.salt,
      '--nav-text': '#6a6a6a',
      '--nav-text-active': '#111111',
      '--nav-active-text': '#111111',
      '--nav-active-bg': '#f2f2f2',
      '--nav-border': '#e6e6e6',
      '--accent': brand.blueberry,
      '--accent-soft': brand.blueberry,
      '--accent-gradient': gradient,
      '--shadow': 'none',
      '--radius': '6px',
      '--chip-bg': '#f2f2f2',
      '--hero-bg': brand.salt,
      '--hero-text': '#111111',
      '--content-radius': '0',
      '--blur': 'none',
    },
    ink: {
      primary: '#111111',
      secondary: '#6a6a6a',
      grid: '#ececec',
      surface: brand.salt,
      series: lightSeries,
    },
  },
  {
    id: 'neon',
    name: 'Neon',
    tagline: 'Deep blueberry night with mint and honey signals and a faint grid.',
    dark: true,
    vars: {
      '--bg': '#0d0b24',
      '--bg-pattern':
        'linear-gradient(rgba(127,184,185,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(127,184,185,0.06) 1px, transparent 1px)',
      '--bg-pattern-size': '32px 32px',
      '--surface': 'rgba(49,39,131,0.28)',
      '--surface-2': 'rgba(49,39,131,0.45)',
      '--surface-border': 'rgba(127,184,185,0.25)',
      '--text': '#eef3ff',
      '--text-muted': '#9fb3c8',
      '--text-faint': '#53607a',
      '--nav-bg': '#07061a',
      '--nav-text': '#7f8db0',
      '--nav-text-active': '#eef3ff',
      '--nav-active-text': '#0d0b24',
      '--nav-active-bg': `linear-gradient(90deg, ${brand.mint}, ${brand.honey})`,
      '--nav-border': 'rgba(127,184,185,0.15)',
      '--accent': brand.mint,
      '--accent-soft': brand.honey,
      '--accent-gradient': `linear-gradient(90deg, ${brand.mint}, ${brand.honey})`,
      '--shadow': '0 0 0 1px rgba(127,184,185,0.18), 0 0 40px rgba(127,184,185,0.12)',
      '--radius': '12px',
      '--chip-bg': 'rgba(127,184,185,0.12)',
      '--hero-bg': '#07061a',
      '--hero-text': '#eef3ff',
      '--content-radius': '0',
      '--blur': 'blur(12px)',
    },
    ink: {
      primary: '#eef3ff',
      secondary: '#9fb3c8',
      grid: 'rgba(127,184,185,0.14)',
      surface: '#1a1740',
      series: ['#7fb8b9', '#e4ae50', '#d49ad4', '#8d85e3', '#f0855f'],
    },
  },
];

export const DEFAULT_THEME: ThemeId = 'soft';
export const THEME_STORAGE_KEY = 'nexus-ui.theme';

export const isThemeId = (value: unknown): value is ThemeId =>
  typeof value === 'string' && THEMES.some(({ id }) => id === value);
